// The fixed set of downloadable-part KINDS a song can carry beyond its master (ART-218). A release track is
// a Master + N variants (Dolby Atmos deliverables, an instrumental, an acapella, stems…), and the authoring
// side offers these as named choices rather than a free-text box — the public share renders each one as a
// chip. Storage stays free-text `Label` (no migration): a picked kind stores its display label, and "Custom"
// is the escape hatch for anything not on the list. Match a stored label back to a kind for display grouping.

export interface VariantKind {
  /** Stable key for the picker option (never stored — the label is what lands on the row). */
  key: string;
  /** The stored, displayed label. Kept short so the public chip reads cleanly when upper-cased. */
  label: string;
}

/** The offered kinds, in picker order (the common two first, then the Atmos family, then stems). */
export const VARIANT_KINDS: readonly VariantKind[] = [
  { key: "instrumental", label: "Instrumental" },
  { key: "acapella", label: "Acapella" },
  { key: "atmos_adm", label: "Atmos ADM" },
  { key: "atmos_binaural", label: "Atmos Binaural" },
  { key: "atmos_ir", label: "Atmos IR" },
  { key: "spatial", label: "Spatial" },
  { key: "stems", label: "Stems" },
];

/** The escape-hatch option: reveals a free-text field, stored verbatim as the label. */
export const CUSTOM_VARIANT_KEY = "custom";

/** The picker options: the fixed kinds plus Custom. */
export const VARIANT_KIND_OPTIONS: readonly { value: string; label: string }[] = [
  ...VARIANT_KINDS.map((k) => ({ value: k.key, label: k.label })),
  { value: CUSTOM_VARIANT_KEY, label: "Custom…" },
];

/** The label a picked kind stores (the custom key resolves to the caller's free text elsewhere). */
export function labelForVariantKey(key: string): string {
  return VARIANT_KINDS.find((k) => k.key === key)?.label ?? "";
}
